import { useCallback, useEffect, useMemo, useRef } from "react";

import { makeVar } from "@apollo/client";
import { generateRandomUUID } from "@dashboard/library";
import { useLocation, useNavigationType } from "react-router";

import {
  SCORECARD_CREATE_SELECTED,
  SCORECARD_CREATION_STARTED,
  SCORECARD_NEW_QUESTION_CREATED,
  SCORECARD_NEW_QUESTION_STARTED,
  SCORECARD_SAVED,
} from "./constants";

import { SCORECARDS_CHILD_ROUTE } from "@/constants/routes.constants";
import { useTrack } from "@/hooks/useTrack/useTrack";

import { manageStorageItem } from "@/helpers/storage/storage.helpers";
import { useCurrentUser } from "../useCurrentUser/useCurrentUser";
import {
  ScorecardNewQuestionCreatedPayload,
  ScorecardNewQuestionStartedPayload,
  ScorecardSavedPayload,
  UseTrackScorecardCreationFunnelReturn,
} from "./useTrackScorecardCreationFunnel.decl";

export const FUNNEL_ID_SESSION_STORAGE = "scorecard_creation_funnel";

const INITIAL_STEP = 1;
const stepVar = makeVar(INITIAL_STEP);

function getAndIncrementStep(reset?: boolean) {
  const currentStep = reset ? INITIAL_STEP : stepVar();

  stepVar(currentStep + 1);

  return currentStep;
}

/**
 * A custom hook which tracks the scorecard creation funnel
 * events, and also their edition ones, only if they are part
 * of the same user flow that began with the creation of the scorecard
 */
export function useTrackScorecardCreationFunnel(): UseTrackScorecardCreationFunnelReturn {
  const { track } = useTrack();
  const { currentUser } = useCurrentUser();
  const currentUserId = currentUser?.ID ?? "";
  const hasUpdatedFunnelIdDueToNavigation = useRef(false);

  const location = useLocation();
  const navigationType = useNavigationType();

  const {
    setAndReturnNewItem: setAndReturnNewFunnelId,
    getExistingItem: getExistingFunnelId,
    deleteItem: deleteFunnelId,
  } = manageStorageItem(FUNNEL_ID_SESSION_STORAGE, generateRandomUUID);

  useEffect(() => {
    const currentPath = location.pathname;

    /**
     * This is set from ScoreCardNew component, after saving a draft or publishing a scorecard,
     * when redirecting users to the /edit page
     */
    const fromPath = (location.state as { from?: string })?.from;

    /**
     * We delete funnel_id if user reaches edit page
     * from a different page than the /new one
     */
    if (
      fromPath !== SCORECARDS_CHILD_ROUTE.NEW &&
      currentPath.includes("edit")
    ) {
      deleteFunnelId();
    }
  }, [location, deleteFunnelId]);

  useEffect(() => {
    /**
     * If navigationType equals POP, it means the user has
     * either reloaded the page, manually inserted its URL,
     * or landed on it through back and forward browser buttons
     */
    if (
      !hasUpdatedFunnelIdDueToNavigation.current &&
      navigationType === "POP"
    ) {
      /**
       * We create a new funnel_id when the user navigates to the new page
       * manually, as we consider this a new user flow
       */
      if (location.pathname === SCORECARDS_CHILD_ROUTE.NEW) {
        setAndReturnNewFunnelId();
      }

      /**
       * We delete funnel_id when the user navigates to the edit page
       * manually, as we don't want to track these events
       */
      if (location.pathname.includes("edit")) {
        deleteFunnelId();
      }

      hasUpdatedFunnelIdDueToNavigation.current = true;
    }
  }, [
    location.pathname,
    navigationType,
    setAndReturnNewFunnelId,
    deleteFunnelId,
  ]);

  const trackScorecardCreationStarted = useCallback(() => {
    track(SCORECARD_CREATION_STARTED, {
      funnel_id: setAndReturnNewFunnelId(),
      user_id: currentUserId,
      step_id: getAndIncrementStep(true),
    });
  }, [currentUserId, setAndReturnNewFunnelId, track]);

  const trackScorecardCreateSelected = useCallback(() => {
    const funnelId = getExistingFunnelId();

    if (!funnelId) {
      return;
    }

    track(SCORECARD_CREATE_SELECTED, {
      funnel_id: funnelId,
      user_id: currentUserId,
      step_id: getAndIncrementStep(),
    });
  }, [currentUserId, getExistingFunnelId, track]);

  const trackScorecardNewQuestionStarted = useCallback(
    ({ is_ai_question }: ScorecardNewQuestionStartedPayload) => {
      const funnelId = getExistingFunnelId();

      if (!funnelId) {
        return;
      }

      track(SCORECARD_NEW_QUESTION_STARTED, {
        funnel_id: funnelId,
        user_id: currentUserId,
        is_ai_question,
        step_id: getAndIncrementStep(),
      });
    },
    [currentUserId, getExistingFunnelId, track]
  );

  const trackScorecardNewQuestionCreated = useCallback(
    ({ is_ai_question }: ScorecardNewQuestionCreatedPayload) => {
      const funnelId = getExistingFunnelId();

      if (!funnelId) {
        return;
      }

      track(SCORECARD_NEW_QUESTION_CREATED, {
        funnel_id: funnelId,
        user_id: currentUserId,
        is_ai_question,
        step_id: getAndIncrementStep(),
      });
    },
    [currentUserId, getExistingFunnelId, track]
  );

  const trackScorecardSaved = useCallback(
    ({ status, is_success }: ScorecardSavedPayload) => {
      const funnelId = getExistingFunnelId();

      if (!funnelId) {
        return;
      }

      track(SCORECARD_SAVED, {
        funnel_id: funnelId,
        user_id: currentUserId,
        status,
        is_success,
        step_id: getAndIncrementStep(),
      });
    },
    [currentUserId, getExistingFunnelId, track]
  );

  const useTrackAddonSubscriptionFunnelReturn = useMemo(
    () => ({
      trackScorecardCreationStarted,
      trackScorecardCreateSelected,
      trackScorecardNewQuestionStarted,
      trackScorecardNewQuestionCreated,
      trackScorecardSaved,
      deleteFunnelId,
    }),
    [
      trackScorecardCreationStarted,
      trackScorecardCreateSelected,
      trackScorecardNewQuestionStarted,
      trackScorecardNewQuestionCreated,
      trackScorecardSaved,
      deleteFunnelId,
    ]
  );

  return useTrackAddonSubscriptionFunnelReturn;
}
